import React, { useState, useEffect } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const GoogleMapsPicker = ({
  handleGetCoordinate,
  latitude,
  longitude,
  address,
  canEdit = true,
}) => {
  const [marker, setMarker] = useState({
    lat: latitude || null,
    lng: longitude || null,
  });
  const [mapCenter, setMapCenter] = useState({
    lat: latitude || 2.967721913980569,
    lng: longitude || 99.07790422439575,
  });
  const [inputValue, setInputValue] = useState(""); // ✅ Separate state for input typing

  // ✅ Ensure `usePlacesAutocomplete` is only initialized once
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: { types: ["geocode"] },
    debounce: 300,
  });

  // ✅ Sync address from props to local input only when it changes
  useEffect(() => {
    if (address) {
      setInputValue(address);
      setValue(address, false);
    }
  }, [address, setValue]);

  // ✅ Handle user selecting a suggestion
  const handleSelect = async (place) => {
    setValue(place.description, false);
    setInputValue(place.description);
    clearSuggestions();

    try {
      const results = await getGeocode({ address: place.description });
      const { lat, lng } = await getLatLng(results[0]);

      setMarker({ lat, lng });
      setMapCenter({ lat, lng });

      handleGetCoordinate({
        latitude: lat,
        longitude: lng,
        address: place.description,
      });
    } catch (error) {
      console.error("Error fetching coordinates:", error);
    }
  };

  // ✅ Handle user clicking on the map
  const handleMapClick = async (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarker({ lat, lng });
    setMapCenter({ lat, lng });

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
      );
      const data = await response.json();
      const newAddress =
        data.results.length > 0
          ? data.results[0].formatted_address
          : "Unknown Address";

      handleGetCoordinate({
        latitude: lat,
        longitude: lng,
        address: newAddress,
      });

      setInputValue(newAddress);
      setValue(newAddress, false);
    } catch (error) {
      console.error("Failed to retrieve address");
    }
  };

  return (
    <div
      style={{
        textAlign: "center",
        position: "relative",
      }}
      className="w-full"
    >
      {canEdit && (
        <>
          {/* ✅ Search Input */}
          <input
            type="text"
            value={inputValue} // ✅ Keeps user input independent of state updates
            onChange={(e) => {
              setInputValue(e.target.value);
              setValue(e.target.value);
            }}
            disabled={!ready}
            placeholder="Search location..."
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "16px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />

          {/* ✅ Show Suggestions */}
          {status === "OK" && (
            <ul
              style={{
                listStyleType: "none",
                padding: "0",
                margin: "0",
                width: "100%",
                background: "#fff",
                position: "absolute",
                zIndex: 1000,
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                borderRadius: "5px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {data.map((place) => (
                <li
                  key={place.place_id}
                  onClick={() => handleSelect(place)}
                  style={{
                    padding: "10px",
                    cursor: "pointer",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  {place.description}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <div className="w-full">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={10}
          onClick={handleMapClick}
        >
          {marker.lat && <Marker position={marker} />}
        </GoogleMap>
      </div>
    </div>
  );
};

export default GoogleMapsPicker;
